/**
 * Bit-packed binary stream used for all game-channel messages (inputs, snapshots).
 * Little-endian bit order within bytes. Values up to 32 bits per call.
 */
export class BitWriter {
  private buf: Uint8Array;
  private bitPos = 0;

  constructor(initialBytes = 256) {
    this.buf = new Uint8Array(initialBytes);
  }

  private ensure(bits: number) {
    const needed = Math.ceil((this.bitPos + bits) / 8);
    if (needed > this.buf.length) {
      const next = new Uint8Array(Math.max(needed, this.buf.length * 2));
      next.set(this.buf);
      this.buf = next;
    }
  }

  bits(value: number, count: number): this {
    this.ensure(count);
    value = value >>> 0;
    for (let i = 0; i < count; i++) {
      if ((value >>> i) & 1) this.buf[this.bitPos >> 3]! |= 1 << (this.bitPos & 7);
      this.bitPos++;
    }
    return this;
  }

  bool(v: boolean): this {
    return this.bits(v ? 1 : 0, 1);
  }

  u8(v: number) {
    return this.bits(v & 0xff, 8);
  }
  u16(v: number) {
    return this.bits(v & 0xffff, 16);
  }
  u32(v: number) {
    return this.bits(v >>> 0, 32);
  }

  /** Signed integer in `count` bits (two's complement). */
  int(v: number, count: number): this {
    const mask = count === 32 ? 0xffffffff : (1 << count) - 1;
    return this.bits(v & mask, count);
  }

  /** Unsigned LEB-style varint in 4-bit groups — cheap for small counts. */
  varuint(v: number): this {
    do {
      const chunk = v & 0x7;
      v = Math.floor(v / 8);
      this.bits(chunk | (v > 0 ? 0x8 : 0), 4);
    } while (v > 0);
    return this;
  }

  f32(v: number): this {
    F32[0] = v;
    return this.u32(U32[0]!);
  }

  /** Quantize v in [min,max] to `count` bits. */
  quant(v: number, min: number, max: number, count: number): this {
    const steps = count === 32 ? 0xffffffff : (1 << count) - 1;
    const t = v <= min ? 0 : v >= max ? 1 : (v - min) / (max - min);
    return this.bits(Math.round(t * steps), count);
  }

  angle(v: number, count = 16): this {
    const t = (((v / (Math.PI * 2)) % 1) + 1) % 1;
    return this.bits(Math.round(t * ((1 << count) - 1)), count);
  }

  bytes(data: Uint8Array): this {
    this.varuint(data.length);
    for (const b of data) this.u8(b);
    return this;
  }

  string(s: string): this {
    return this.bytes(TEXT_ENC.encode(s));
  }

  get bitLength() {
    return this.bitPos;
  }

  finish(): Uint8Array {
    return this.buf.slice(0, Math.ceil(this.bitPos / 8));
  }
}

export class BitReader {
  private bitPos = 0;
  private readonly bitLen: number;

  constructor(private readonly buf: Uint8Array) {
    this.bitLen = buf.length * 8;
  }

  get remainingBits() {
    return this.bitLen - this.bitPos;
  }

  bits(count: number): number {
    if (this.bitPos + count > this.bitLen) throw new RangeError("BitReader overrun");
    let v = 0;
    for (let i = 0; i < count; i++) {
      if ((this.buf[this.bitPos >> 3]! >> (this.bitPos & 7)) & 1) v |= 1 << i;
      this.bitPos++;
    }
    return v >>> 0;
  }

  bool() {
    return this.bits(1) === 1;
  }
  u8() {
    return this.bits(8);
  }
  u16() {
    return this.bits(16);
  }
  u32() {
    return this.bits(32);
  }

  int(count: number): number {
    const v = this.bits(count);
    if (count === 32) return v | 0;
    const sign = 1 << (count - 1);
    return v & sign ? v - (1 << count) : v;
  }

  varuint(): number {
    let v = 0;
    let mul = 1;
    for (;;) {
      const chunk = this.bits(4);
      v += (chunk & 0x7) * mul;
      if (!(chunk & 0x8)) return v;
      mul *= 8;
    }
  }

  f32(): number {
    U32[0] = this.u32();
    return F32[0]!;
  }

  quant(min: number, max: number, count: number): number {
    const steps = count === 32 ? 0xffffffff : (1 << count) - 1;
    return min + (this.bits(count) / steps) * (max - min);
  }

  angle(count = 16): number {
    let a = (this.bits(count) / ((1 << count) - 1)) * Math.PI * 2;
    if (a > Math.PI) a -= Math.PI * 2;
    return a;
  }

  bytes(): Uint8Array {
    const n = this.varuint();
    const out = new Uint8Array(n);
    for (let i = 0; i < n; i++) out[i] = this.u8();
    return out;
  }

  string(): string {
    return TEXT_DEC.decode(this.bytes());
  }
}

const F32 = new Float32Array(1);
const U32 = new Uint32Array(F32.buffer);
const TEXT_ENC = new TextEncoder();
const TEXT_DEC = new TextDecoder();
